// components/HomePage/Contact.jsx
//
// The closing block of the homepage: an invitation to start a project.
// It deliberately does NOT duplicate the /contact form. The site already
// has one working form; two competing forms is worse UX, and the old
// version of this section only pretended to send. What lives here now is
// the invitation, the brand mark, and the channels that actually reach a
// person.
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Award,
  ClipboardList,
  Headphones,
  Mail,
  Phone,
  ShieldCheck,
  Users,
} from "lucide-react";

import Section, { SectionHeading } from "../common/Section";
import Reveal from "../common/Reveal";
import { WhatsAppLink } from "../common/WhatsAppButton";
import { CONTACT, MAILTO, TEL } from "../../config/site";
import {
  SHOW_247_SUPPORT,
  SHOW_AWARD_CLAIMS,
  SHOW_PUBLIC_PRICING,
} from "../../config/claims";
import { SERVICES, SERVICE_CATEGORIES } from "../../data/serviceCatalog";
import "./Css/Contact.css";

/** Maximum tilt of the brand mark, in degrees. Small on purpose. */
const MAX_TILT = 7;

// Context-appropriate opener, written in the same voice as
// whatsappUrlFor() in config/site.js — this one is specific to the
// homepage close rather than to a single service page.
const WHATSAPP_MESSAGE =
  "Hi DepthLogix, I'd like to discuss a new project. Could we set up a short call?";

/**
 * The value props that used to be four gradient cards.
 *
 * Two of the original four made claims nothing in the business can
 * substantiate, so they stay here verbatim but are gated off — see
 * config/claims.js. The replacement is backed by real data: every one of
 * the 25 services in the catalog publishes a duration and a price.
 */
const VALUES = [
  {
    icon: Users,
    title: "Expert Team",
    body: "Skilled professionals delivering quality work on every project, across web, mobile, AI systems and business applications.",
    show: true,
  },
  {
    icon: Award,
    title: "Excellence Recognized",
    body: "Award-winning solutions and industry recognition for exceptional quality delivery.",
    show: SHOW_AWARD_CLAIMS,
  },
  {
    icon: ShieldCheck,
    title: "Transparency First",
    body: "Clear communication and honest partnerships built on trust and integrity.",
    show: true,
  },
  {
    icon: ClipboardList,
    title: "Clear Scope Up Front",
    body: SHOW_PUBLIC_PRICING
      ? "Every service on this site lists an indicative timeline and a starting price, so you know the shape of the work before it begins."
      : "Every service on this site lists an indicative timeline, so you know the shape of the work before it begins.",
    show: true,
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    body: "Always here when you need us, providing round-the-clock dedicated assistance.",
    show: SHOW_247_SUPPORT,
  },
].filter((value) => value.show);

/** The two channels that are plain links; WhatsApp gets its own button. */
const CHANNELS = [
  { icon: Mail, label: "Email", value: CONTACT.email, href: MAILTO },
  { icon: Phone, label: "Call", value: CONTACT.phoneDisplay, href: TEL },
];

const Contact = () => {
  const stageRef = useRef(null);
  const markRef = useRef(null);

  /**
   * Pointer tilt on the brand mark.
   *
   * Kept from the original section because it is the one genuinely
   * distinctive interaction down here — but rebuilt:
   *  - stable handler references, so the cleanup actually removes them
   *  - writes the transform straight to the node inside a single rAF, so
   *    pointer movement never re-renders React
   *  - fine-pointer only (it did nothing on touch and never could)
   *  - opted out entirely under prefers-reduced-motion
   */
  useEffect(() => {
    const stage = stageRef.current;
    const mark = markRef.current;
    if (!stage || !mark || typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!finePointer.matches || reducedMotion.matches) return undefined;

    let frame = 0;
    let tiltX = 0;
    let tiltY = 0;

    const paint = () => {
      frame = 0;
      mark.style.transform = `rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(paint);
    };

    const handlePointerMove = (event) => {
      const rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      // Normalised -1..1 offset from the centre of the stage.
      const nx = (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const ny = (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      tiltX = Math.max(-1, Math.min(1, -ny)) * MAX_TILT;
      tiltY = Math.max(-1, Math.min(1, nx)) * MAX_TILT;
      schedule();
    };

    const handlePointerLeave = () => {
      tiltX = 0;
      tiltY = 0;
      schedule();
    };

    stage.addEventListener("pointermove", handlePointerMove);
    stage.addEventListener("pointerleave", handlePointerLeave);

    return () => {
      stage.removeEventListener("pointermove", handlePointerMove);
      stage.removeEventListener("pointerleave", handlePointerLeave);
      if (frame) window.cancelAnimationFrame(frame);
      mark.style.transform = "";
    };
  }, []);

  return (
    <Section
      id="contact"
      tone="subtle"
      className="grid-field border-t border-ink-200/70"
      aria-labelledby="home-contact-heading"
    >
      {/* ---------- The invitation ---------- */}
      <SectionHeading
        headingId="home-contact-heading"
        eyebrow="Get in touch"
        title="Let's discuss your project."
        lead="We build innovative, AI-driven technology solutions for businesses: high-performance, user-focused applications that drive growth. Tell us what you're building, and we'll come back with how we would approach it."
      />

      <Reveal delay={90} className="mt-9">
        <Link to="/contact" className="btn-primary group w-full sm:w-auto">
          Start a project
          <ArrowRight
            aria-hidden="true"
            className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-0.5"
          />
        </Link>
      </Reveal>

      {/* ---------- Brand mark + direct lines ---------- */}
      <div className="mt-14 grid items-stretch gap-6 md:mt-20 lg:grid-cols-[0.92fr_1.08fr] lg:gap-10">
        <Reveal className="flex">
          <div
            ref={stageRef}
            className="dl-mark-stage flex w-full flex-col items-center justify-center rounded-panel border border-ink-200/80 bg-gradient-to-b from-white to-brand-50/70 px-6 py-10 shadow-card sm:px-10 sm:py-14"
          >
            <div ref={markRef} className="dl-mark w-full max-w-[15rem] sm:max-w-[17rem]">
              <img
                src="/BG/logopng3D.png"
                alt="DepthLogix"
                width={730}
                height={696}
                loading="lazy"
                decoding="async"
                className="h-auto w-full"
              />
            </div>

            <div className="mt-9 h-px w-16 bg-ink-200" aria-hidden="true" />

            {/* Real counts, straight out of the service catalog. */}
            <p className="mt-5 text-center font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-500">
              {SERVICES.length} services / {SERVICE_CATEGORIES.length} practices
            </p>
          </div>
        </Reveal>

        {/* Channels that actually reach a person. */}
        <Reveal delay={90} className="flex">
          <div className="w-full rounded-panel border border-ink-200/80 bg-white p-5 shadow-panel sm:p-8 lg:p-10">
            <h3 className="font-bricolage text-xl font-semibold tracking-[-0.02em] text-ink-900 sm:text-2xl">
              Direct lines
            </h3>
            <p className="mt-3 max-w-[46ch] leading-relaxed text-ink-500">
              Prefer to talk it through before filling anything in? Every one of
              these reaches us directly.
            </p>

            {/* Kept short so it never wraps inside the panel at 320px. */}
            <WhatsAppLink message={WHATSAPP_MESSAGE} className="mt-7 w-full">
              Chat on WhatsApp
            </WhatsAppLink>

            <ul className="mt-7 border-t border-ink-100 pt-3">
              {CHANNELS.map(({ icon: Icon, label, value, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    className="group -mx-3 flex items-center gap-4 rounded-xl px-3 py-3.5 transition-colors duration-200 hover:bg-brand-50"
                  >
                    <span className="grid h-11 w-11 flex-none place-items-center rounded-xl border border-ink-200 bg-white text-brand-700 transition-colors duration-200 group-hover:border-brand-300">
                      <Icon aria-hidden="true" className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-[0.65rem] uppercase tracking-eyebrow text-ink-500">
                        {label}
                      </span>
                      <span className="mt-0.5 block break-words text-[0.95rem] font-semibold text-ink-900 transition-colors duration-200 group-hover:text-brand-700 sm:text-base">
                        {value}
                      </span>
                    </span>
                    <ArrowUpRight
                      aria-hidden="true"
                      className="ml-auto h-4 w-4 flex-none text-ink-300 transition-[color,transform] duration-200 ease-out-expo group-hover:-translate-y-0.5 group-hover:text-brand-600"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      {/* ---------- What holds true either way ---------- */}
      <div className="mt-14 md:mt-20">
        <div className="rule" aria-hidden="true" />
        <div className="mt-10 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {VALUES.map(({ icon: Icon, title, body }, index) => (
            <Reveal
              key={title}
              delay={index * 80}
              className="border-t border-ink-200 pt-6"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-[0.7rem] tracking-eyebrow text-brand-600">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <Icon aria-hidden="true" className="h-4 w-4 text-brand-700" />
              </div>
              <h3 className="mt-4 font-bricolage text-lg font-semibold tracking-[-0.02em] text-ink-900">
                {title}
              </h3>
              <p className="mt-2.5 max-w-[42ch] text-[0.95rem] leading-relaxed text-ink-500">
                {body}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default Contact;
